import { motion, useAnimation } from "motion/react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

const HAND_VARIANTS = {
  normal: {
    rotate: 0,
    originX: "0%",
    originY: "100%",
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  animate: {
    rotate: 360,
    originX: "0%",
    originY: "100%",
    transition: {
      delay: 0.1,
      duration: 1.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const BUTTON_VARIANTS = {
  normal: {
    scale: 1,
    y: 0,
  },
  animate: {
    scale: [0.9, 1],
    y: [0, 1, 0],
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const TimerIcon = forwardRef(function TimerIcon(
  { onMouseEnter, onMouseLeave, size = 18, ...props },
  ref
) {
  const controls = useAnimation();
  const isControlledRef = useRef(false);

  useImperativeHandle(ref, () => {
    isControlledRef.current = true;
    return {
      startAnimation: () => {
        controls.set("normal");
        void controls.start("animate");
      },
      stopAnimation: () => controls.set("normal"),
    };
  }, [controls]);

  const handleMouseEnter = useCallback((event) => {
    if (isControlledRef.current) {
      onMouseEnter?.(event);
    } else {
      void controls.start("animate");
    }
  }, [controls, onMouseEnter]);

  const handleMouseLeave = useCallback((event) => {
    if (isControlledRef.current) {
      onMouseLeave?.(event);
    } else {
      void controls.start("normal");
    }
  }, [controls, onMouseLeave]);

  return (
    <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>
      <svg
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <motion.line
          animate={controls}
          initial="normal"
          variants={BUTTON_VARIANTS}
          x1="10"
          x2="14"
          y1="2"
          y2="2"
        />
        <motion.line
          animate={controls}
          initial="normal"
          variants={HAND_VARIANTS}
          x1="12"
          x2="15"
          y1="14"
          y2="11"
        />
        <circle cx="12" cy="14" r="8" />
      </svg>
    </span>
  );
});

function RepeatingTimerIcon(props) {
  const iconRef = useRef(null);

  useEffect(() => {
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let intervalId = null;

    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      iconRef.current?.stopAnimation();
    };

    const start = () => {
      if (intervalId !== null || reducedMotionQuery.matches) return;
      iconRef.current?.startAnimation();
      intervalId = window.setInterval(() => {
        iconRef.current?.startAnimation();
      }, 2500);
    };

    const syncWithPreference = () => {
      if (reducedMotionQuery.matches) stop();
      else start();
    };

    syncWithPreference();
    if (reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener("change", syncWithPreference);
    } else {
      reducedMotionQuery.addListener?.(syncWithPreference);
    }

    return () => {
      stop();
      if (reducedMotionQuery.removeEventListener) {
        reducedMotionQuery.removeEventListener("change", syncWithPreference);
      } else {
        reducedMotionQuery.removeListener?.(syncWithPreference);
      }
    };
  }, []);

  return <TimerIcon ref={iconRef} {...props} />;
}

export { RepeatingTimerIcon, TimerIcon };
